import { defaultPageContent, fontList } from '../const';

import { standardApi, wceApi } from '@/api';
import { getCachedDesignEditor } from './design';

type GetMallsResponse = {
  accumulationConfig: {
    accumulationDisplayFormatType: string | null;
    accumulationGivePoint: string;
    accumulationName: string | null;
    accumulationRate: number;
    accumulationUnit: string | null;
    accumulationUseMaxRate: number;
    accumulationUseMinPrice: number;
    accumulationUseMinProductPrice: number;
    accumulationValidPeriod: number;
    adminMemo: string | null;
    birthdayAccumulation: string | null;
    excludingReservePayAccumulation: boolean;
    excludingReservePayCoupon: boolean;
    expireNotificationPoint: number;
    limitMaxRate: boolean;
    limitMinPrice: boolean;
    limitMinProductPrice: boolean;
    productAccumulationBasisType: string | null;
    reviewsAccumulationDetail: {
      photoReviewsAccumulation: number;
      photoReviewsLength: number;
      reviewsAccumulation: number;
      reviewsLength: number;
    };
    signUpAccumulation: number;
    useExpireNotification: boolean;
    useMemberAccumulation: boolean;
    useProductAccumulation: boolean;
    useReviewsAccumulation: boolean;
    useSignUpAccumulation: boolean;
    useBirthdayAccumulation: boolean;
  };
  bankAccountInfo: {
    bank: string;
    bankAccount: string;
    bankDepositorName: string;
    bankName: string;
  };
  bankAccountInfos: {
    bank: string;
    bankAccount: string;
    bankDepositorName: string;
    bankName: string;
  }[];
  bankType: {
    code: string;
    kcpCode: string;
    name: string;
    value: string;
  }[];
  boardsCategories: {
    attachmentUsed: boolean;
    boardId: string | null;
    boardName: string;
    boardNo: number;
    categories: any[];
    categoryUsed: boolean;
    displayType: string;
    guestWrite: boolean;
    imageDisplayType: string;
    memberWrite: boolean;
    replied: boolean;
    secreted: boolean;
    thumbnailUsed: boolean;
    used: boolean;
  }[];
  cartConfig: {
    cartEquivalentOptionUnitType: string;
    storageMaxQuantity: number;
    storagePeriod: number;
    storagePeriodNoLimit: boolean;
  };
  categories: {
    flatCategories: {
      depth1CategoryNo: number;
      depth1Content: string;
      depth1DisplayOrder: number;
      depth1Icon: string | null;
      depth1Label: string;
      depth2CategoryNo: number;
      depth2Content: string | null;
      depth2DisplayOrder: number;
      depth2Icon: string | null;
      depth2Label: string;
      depth3CategoryNo: number;
      depth3Content: string | null;
      depth3DisplayOrder: number;
      depth3Icon: string | null;
      depth3Label: string;
      depth4CategoryNo: number;
      depth4Content: string | null;
      depth4DisplayOrder: number;
      depth4Icon: string | null;
      depth4Label: string;
      depth5CategoryNo: number;
      depth5Content: string;
      depth5DisplayOrder: number;
      depth5Icon: string | null;
      depth5Label: string;
      fullCategoryName: string;
    }[];
    multiLevelCategories: {
      categoryNo: number;
      children: any[];
      content: string;
      depth: number;
      icon: string;
      label: string;
    }[];
  };
  claimReasonType: {
    label: string;
    value: string;
  }[];
  claimStatusType: {
    label: string;
    value: string;
  }[];
  externalServiceConfig: {
    googleAnalytics: string;
    kakaoMap: {
      kakaoMapKey: string;
      kakaoMapLatitude: string;
      kakaoMapLongitude: string;
    };
    naverWebmaster: string;
    useScript: boolean;
  };
  inquiryType: any[];
  instagramUsed: boolean;
  mall: {
    countryCode: string;
    createdDateTime: string;
    escrowLogo: string | null;
    globalMallHierarchy: string | null;
    globalMallType: string;
    globalSubMallSummaries: any[];
    godoSno: string | null;
    grades: {
      accumulationRate: number;
      coupons: any[];
      description: string;
      evaluationCondition: {
        minimumCount: number;
        minimumPayment: number;
      };
      gradeDescription: string;
      label: string;
      minOrderAmt: number;
      minOrderCnt: number;
      pointRate: number;
      reserveAutoSupplying: {
        amount: number;
        type: string;
        used: boolean;
      };
      reserveBenefit: {
        reserveRate: number;
        used: boolean;
      };
      used: boolean;
    }[];
    introRedirection: {
      mobile: string;
      pc: string;
    };
    mallName: string;
    mallNo: number;
    serviceCenter: {
      email: string;
      phoneNo: string;
    };
    url: {
      android: string;
      ios: string;
      mobile: string;
      pc: string;
    };
  };
  mallJoinConfig: {
    authenticationType: string;
    authenticationTimeType: string;
  };
  memberJoinConfig: {
    address: string;
    birthday: string;
    email: string;
    emailAgreement: string;
    memberId: string;
    memberName: string;
    mobileNo: string;
    nickname: string;
    password: string;
    phoneNo: string;
    sex: string;
    smsAgreement: string;
  };
  openIdJoinConfig: {
    authenticationType: string;
    authenticationTimeType: string;
    providers: string[];
  };
  orderStatusType: {
    label: string;
    value: string;
  }[];
  productInquiryType: {
    label: string;
    value: string;
  }[];
  productReviewReportType: {
    label: string;
    value: string;
  }[];
  serviceBasicInfo: {
    address: string;
    addressDetail: string;
    businessCondition: string | null;
    businessRegistrationNo: string;
    businessType: string | null;
    companyName: string;
    faxNo: string | null;
    jibunAddress: string;
    jibunAddressDetail: string;
    onlineMarketingBusinessDeclarationNo: string;
    plan: string;
    privacyManagerName: string;
    privacyManagerPhoneNo: string;
    representEmail: string;
    representPhoneNo: string;
    representativeName: string;
    zipCd: string;
  };
  termsConfig: {
    fairLogoUrl: string | null;
    fairLogoUsed: boolean;
  };
};

let mallInfoCache: GetMallsResponse;

const fetchMallInfo = async () => {
  if (mallInfoCache) {
    return Promise.resolve(mallInfoCache);
  }

  const clientId = await new Promise<string>((resolve) => {
    getCachedDesignEditor(({ clientId }) => {
      resolve(clientId);
    });
  });

  const { data } = await standardApi.get<GetMallsResponse>('/malls', {
    headers: {
      clientId,
    },
  });

  mallInfoCache = data;

  return data;
};

export const fetchFontList = (cb: Function) => {
  cb(fontList);
};

export const changeFontFamily = (fontFamily: string) => {
  wceApi.put('/design/shops/font-family', { fontFamily });
};

export const fetchPrimaryShopinfo = (cb: Function) => {
  fetchMallInfo()
    .then(({
      serviceBasicInfo: {
        companyName,
        representativeName,
        privacyManagerName,
        representPhoneNo,
        representEmail,
        address,
        addressDetail,
        businessRegistrationNo,
        onlineMarketingBusinessDeclarationNo,
      },
      mall: {
        mallName,
      },
    }) => {
      cb({
        businessName: companyName,
        representative: representativeName,
        infomationManager: privacyManagerName,
        phone: representPhoneNo,
        email: representEmail,
        address: `${address}${addressDetail}`,
        registrationNumber: businessRegistrationNo,
        mailOrderInfo: onlineMarketingBusinessDeclarationNo,
        hostingOffers: '엔에이치엔커머스',
        copyright: `Copyright @${mallName} All rights reserved.`,
      });
    });
};

export const changeTemplateSkin = (templateId: string, cb: Function) => {
  wceApi.get(`/design/shops/templates/${templateId}`)
    .then(({
      data: {
        fontFamily,
        menus: {
          linked,
          unlinked,
        },
        pageInfos,
      },
    }) => {
      const entities = pageInfos.reduce((acc, { content, pageId, title, url }) => {
        acc[pageId] = {
          content: content && JSON.parse(content) || defaultPageContent(),
          title,
          url,
        };

        return acc;
      }, {});

      cb({
        page: {
          entities,
          fontFamily: fontFamily || fontList[0].fontFamily,
          linked,
          unlinked,
        },
      });
    });
};

export const setFixedTemplate = (templateId: string, cb: Function) => {
  wceApi.put('/design/shops/template', { templateId })
    .then(() => {
      if (typeof cb === 'function') {
        cb(true);
      }
    })
    .catch(() => {
      if (typeof cb === 'function') {
        cb(false);
      }
    });
};